import { isChoice, normalizeLabel, truthy } from './fieldTypes';

/** A saved field as the public "complete" endpoint returns it (booleans rather than 0/1). */
export const asPublicField = (field) => ({
  ...field,
  is_required: truthy(field.is_required),
  isVisible: field.isVisible === undefined ? true : truthy(field.isVisible),
  allow_other: truthy(field.allow_other),
  is_workshop: truthy(field.is_workshop),
});

/** Card title, button text and the language of the page's own messages, from the form's language settings. */
export const formInfoFrom = (language) => ({
  language_id: language?.language_id ?? null,
  application_text: language?.application_text || null,
  submit_text: language?.submit_text || null,
});

const capacityLookup = (saved, label) => {
  const cap = saved?.workshop_capacity;
  if (!cap) return null;
  return cap[label] || cap[normalizeLabel(label)] || null;
};

/**
 * The question being edited, in the shape the public page renders. Workshop places keep the
 * bookings already made, so an option shows as full exactly when it will be after saving.
 */
export const draftToPublicField = (draft, saved, order) => {
  const choice = isChoice(draft.field_type);
  const labels = choice ? draft.options.map((o) => o.label.trim()).filter(Boolean) : null;
  const field = {
    field_id: draft.field_id ?? '__draft__',
    field_name: draft.field_name || '__draft__',
    display_label: draft.display_label?.trim() || null,
    field_description: draft.field_description || null,
    field_type: draft.field_type,
    is_required: !!draft.is_required,
    isVisible: !!draft.isVisible,
    field_order: saved?.field_order ?? order,
    allowed_file_types: draft.field_type === 'file' ? draft.allowed_file_types || null : null,
    max_file_size_mb: draft.field_type === 'file' ? draft.max_file_size_mb || null : null,
    field_options: labels,
    allow_other: choice && !draft.is_workshop && !!draft.allow_other,
    other_option_label: draft.other_option_label?.trim() || 'Other',
    is_workshop: choice && !!draft.is_workshop,
    min_selections: draft.field_type === 'checkbox' && !draft.is_workshop ? draft.min_selections || null : null,
    max_selections: draft.field_type === 'checkbox' && !draft.is_workshop ? draft.max_selections || null : null,
  };
  if (choice && draft.is_workshop) {
    field.workshop_capacity = {};
    draft.options.forEach((o) => {
      const label = o.label.trim();
      if (!label) return;
      const before = capacityLookup(saved, label);
      const current = before?.current_pax || 0;
      const max = o.max_pax ?? before?.max_pax ?? null;
      field.workshop_capacity[label] = {
        max_pax: max,
        current_pax: current,
        available: max == null ? null : Math.max(0, max - current),
        is_full: max != null && current >= max,
      };
    });
  }
  return field;
};

/** The page data for the preview: { form, layout, fields } as /client-portal/:slug receives it. */
export const previewData = ({ language, layout, fields }) => ({
  form: formInfoFrom(language),
  layout: layout || null,
  fields: [...fields].sort((a, b) => a.field_order - b.field_order),
});
